<script>
    // Rise of Firstborn → War and Peace migration page. Deliberately outside the shop and not
    // linked from it: RFB players get the /firstborn URL and their token in an RFB in-game mail.
    //
    // Contract (web-shop PR #40 → hmmo-backend PR #393, Bogdan's frontend handoff):
    //   POST /api/store/player-state            → { ...playerState, migrationRewardsStatus }
    //        can_claim | claimed | wrong_kingdom; absent while the backend half is not deployed
    //   POST /api/store/claim-migration-rewards  { token } → {} on success, else an HTTP failure
    //        whose message claimFailure() maps (see src/lib/rfb-migration.js)
    // The token must never reach a URL, analytics, the console or Rollbar.
    import { _, isLoading } from 'svelte-i18n';
    import { user, clearExpiredSession, endExpiredSession, isSessionExpired } from './stores';
    import { navigate } from './lib/router/router.js';
    import Login from './Login.svelte';
    import Modal from './lib/Modal.svelte';
    import Loader from './lib/Loader.svelte';
    import { claim_migration_rewards_route, get_player_state_route, logout_route, game_name } from './constants.js';
    import { logMetric } from './metrics.js';
    import { tagNetworkFailure, tagBodyReadFailure } from './lib/network-failure.js';
    import {
        normalizeMigrationToken,
        isValidMigrationToken,
        hasNonHexCharacters,
        MIGRATION_TOKEN_LENGTH,
        migrationView,
        claimFailure,
        RFB_COPY,
        MIGRATION_PAGE_PATH,
        CLAIM_WELCOME_AFTER_MS,
    } from './lib/rfb-migration.js';

    const t = (key, values) => $_(key, { default: RFB_COPY[key], values });

    // 'loading' | 'status_error' | migrationView(): 'can_claim' | 'claimed' | 'wrong_kingdom' | 'not_open'
    let view = $state('loading');
    let tokenInput = $state('');
    let submitting = $state(false);
    let errorKey = $state(null);
    let showSuccess = $state(false);
    // The running claim (settles, never rejects). Leaving for supplies waits for it: after an
    // early welcome the grant may still hold locks on the account, and a get-packs sent in that
    // window failed on staging.
    let pendingClaim = null;
    let leaving = $state(false);

    let token = $derived(normalizeMigrationToken(tokenInput));
    let canClaim = $derived(isValidMigrationToken(token) && !submitting);
    let userId = $derived($user?.user?.id);
    let username = $derived($user?.user?.username ?? '');

    $effect(() => {
        if (!userId) return;
        checkStatus();
    });

    // Re-read player-state without blanking the page; `fallback` is the view to keep when the
    // read itself fails.
    function refreshStatus(fallback) {
        return fetch(get_player_state_route, {
            method: 'POST',
            credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        })
            .catch(tagNetworkFailure)
            .then((response) => {
                if (isSessionExpired(response)) {
                    clearExpiredSession();
                    return null;
                }
                if (!response.ok) throw new Error(`Player state failed: ${response.status}`);
                return response.json().catch(tagBodyReadFailure);
            })
            .then((data) => {
                view = migrationView(data);
            })
            .catch((error) => {
                if (error?.isNetworkFailure) {
                    Rollbar.info('Migration rewards status network failure: ' + error);
                } else {
                    Rollbar.warning('Migration rewards status error: ' + error);
                }
                view = fallback;
            });
    }

    function checkStatus() {
        view = 'loading';
        return refreshStatus('status_error');
    }

    // Only a player-state that says so counts: when the re-read itself fails, assume the claim
    // did not land, so the player can retry (a resubmit by the owner returns the saved result).
    async function confirmClaimed() {
        await refreshStatus('can_claim');
        return view === 'claimed';
    }

    function claim() {
        if (!canClaim) return;
        submitting = true;
        errorKey = null;
        let failure = null;

        let welcomedEarly = false;
        const welcomeTimer = setTimeout(() => {
            welcomedEarly = true;
            showSuccess = true;
            view = 'claimed';
        }, CLAIM_WELCOME_AFTER_MS);
        // The token stays in the field until the answer, so a late failure lands back on the form.
        const undoEarlyWelcome = () => {
            showSuccess = false;
            if (view === 'claimed') view = 'can_claim';
        };

        pendingClaim = fetch(claim_migration_rewards_route, {
            method: 'POST',
            credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ token }),
        })
            .catch(tagNetworkFailure)
            .then(async (response) => {
                clearTimeout(welcomeTimer);
                if (isSessionExpired(response)) {
                    endExpiredSession(claim_migration_rewards_route);
                    return;
                }
                if (response.ok) {
                    if (!welcomedEarly) showSuccess = true;
                    tokenInput = '';
                    await refreshStatus('claimed');
                    return;
                }
                const body = await response.json().catch(() => null);
                failure = body?.message ?? `HTTP ${response.status}`;
                const outcome = claimFailure(response.status, body?.message);
                // A refusal is the player's to fix; anything unmapped is ours to look at.
                if (outcome.key === 'PleaseTryAgainLater') {
                    Rollbar.warning(`Migration rewards claim failed: ${response.status} ${failure}`);
                }
                if (outcome.refreshStatus && await confirmClaimed()) {
                    // It went through after all: a timeout that committed, or already claimed.
                    if (!welcomedEarly && response.status === 504) showSuccess = true;
                    tokenInput = '';
                    return;
                }
                undoEarlyWelcome();
                errorKey = outcome.key;
            })
            .catch(async (error) => {
                clearTimeout(welcomeTimer);
                failure = String(error);
                if (error?.isNetworkFailure) {
                    Rollbar.info('Migration rewards claim network failure: ' + error);
                    // The request may have reached the server and committed: look before calling it failed.
                    if (await confirmClaimed()) {
                        if (!welcomedEarly) showSuccess = true;
                        tokenInput = '';
                        return;
                    }
                } else {
                    Rollbar.error('Migration rewards claim error: ' + error);
                }
                undoEarlyWelcome();
                errorKey = 'PleaseTryAgainLater';
            })
            .finally(() => {
                logMetric({
                    migrationRewardsClaim: {
                        username,
                        ...(failure !== null && { error: failure }),
                    },
                });
                submitting = false;
                pendingClaim = null;
            });
    }

    function logout() {
        logMetric({ button: 'logout' });
        fetch(logout_route, { method: 'GET' })
            .then(() => {
                localStorage.removeItem('user');
                user.set(null);
            })
            .catch((error) => Rollbar.error('Logout error:' + error));
    }

    async function goToSupplies() {
        if (pendingClaim) {
            leaving = true;
            await pendingClaim;
            leaving = false;
            // A late failure took the welcome back: stay and show it.
            if (view !== 'claimed') return;
        }
        navigate('/supplies');
    }
</script>

{#if !$user}
    <Login returnTo={MIGRATION_PAGE_PATH}>
        <p class="rfb-login-notice">{t('RfbLoginNotice')}</p>
    </Login>
{:else if $isLoading}
    <Loader/>
{:else}
    <div class="rfb-page">
        <div class="rfb-hero">
            <img
                    src="/images/rfb/hero.webp"
                    srcset="/images/rfb/hero-800.webp 800w, /images/rfb/hero.webp 1600w"
                    sizes="100vw"
                    width="1600"
                    height="691"
                    alt="Rise of Firstborn"
            />
        </div>
        <!-- Outside .rfb-hero so the wide-screen edge fade never reaches it. -->
        <a class="rfb-home" href="/supplies" onclick={(e) => { e.preventDefault(); goToSupplies(); }}>
            <img src="/images/logo.png" alt={game_name} title={game_name}/>
        </a>

        <section class="rfb-panel">
            {#if view === 'claimed'}
                <div class="rfb-claimed">
                    <h1>{t('RfbClaimedTitle')}</h1>
                    <p class="rfb-intro">{t('RfbClaimedText')}</p>
                    <button type="button" class="rfb-cta" onclick={goToSupplies} disabled={leaving}>
                        {leaving ? t('RfbFinishingClaim') : t('RfbGoToSupplies')}
                    </button>
                </div>
            {:else}
                <h1>{t('RfbWelcomeTitle')}</h1>
                <p class="rfb-intro">{t('RfbWelcomeText')}</p>

                <h2>{t('RfbStepsTitle')}</h2>
                <ol class="rfb-steps">
                    <li>{t('RfbStepAccount')}</li>
                    <li>{t('RfbStepKingdom')}</li>
                    <li>{t('RfbStepToken')}</li>
                </ol>

                {#if view === 'loading'}
                    <div class="rfb-loading"><Loader/></div>
                {:else if view === 'wrong_kingdom'}
                    <div class="rfb-notice rfb-wrong-kingdom">
                        <h2>{t('RfbWrongKingdomTitle')}</h2>
                        <p>{t('RfbWrongKingdomText')}</p>
                        <button type="button" class="rfb-secondary" onclick={checkStatus}>{t('RfbCheckAgain')}</button>
                    </div>
                {:else if view === 'not_open'}
                    <div class="rfb-notice rfb-not-open">
                        <h2>{t('RfbNotOpenTitle')}</h2>
                        <p>{t('RfbNotOpenText')}</p>
                    </div>
                {:else if view === 'status_error'}
                    <div class="rfb-notice rfb-status-error">
                        <p>{t('RfbStatusErrorText')}</p>
                        <button type="button" class="rfb-secondary" onclick={checkStatus}>{t('RfbCheckAgain')}</button>
                    </div>
                {:else}
                    <form class="rfb-form" onsubmit={(e) => { e.preventDefault(); claim(); }}>
                        <label for="rfb-token">{t('RfbTokenPlaceholder')}</label>
                        <div class="rfb-field" class:complete={isValidMigrationToken(token)}>
                            <input
                                    id="rfb-token"
                                    type="text"
                                    bind:value={tokenInput}
                                    autocomplete="off"
                                    autocorrect="off"
                                    autocapitalize="characters"
                                    spellcheck="false"
                                    maxlength="24"
                                    aria-describedby="rfb-token-hint"
                                    aria-invalid={errorKey !== null || hasNonHexCharacters(token)}
                            />
                            <span class="rfb-count" aria-hidden="true">{token.length}/{MIGRATION_TOKEN_LENGTH}</span>
                        </div>
                        <p id="rfb-token-hint" class="rfb-hint">{t('RfbTokenHint')}</p>
                        {#if hasNonHexCharacters(token)}
                            <p class="rfb-token-warning" role="status">{t('RfbTokenBadCharacters')}</p>
                        {/if}
                        {#if errorKey}
                            <p class="rfb-error" role="alert">{t(errorKey)}</p>
                        {/if}
                        <button type="submit" class="rfb-cta rfb-claim" disabled={!canClaim}>
                            {submitting ? t('RfbClaiming') : t('RfbClaim')}
                        </button>
                        <p class="rfb-account">
                            {t('RfbClaimingFor', { player: username })}
                            <a href="#logout" onclick={(e) => { e.preventDefault(); logout(); }}>{t('RfbNotYou')}</a>
                        </p>
                    </form>
                {/if}
            {/if}
        </section>

        <img class="rfb-heroes" src="/images/rfb/heroes.webp" width="1600" height="364" alt="" loading="lazy"/>
    </div>

    <Modal bind:open={showSuccess} close={() => (showSuccess = false)}>
        <div class="card howto rfb-result">
            <h2>{t('RfbSuccessTitle')}</h2>
            <p>{t('RfbSuccessText')}</p>
            <button type="button" class="button primary blue" onclick={() => (showSuccess = false)}>{$_('Ok')}</button>
        </div>
    </Modal>
{/if}

<style lang="scss">
  .rfb-page {
    --rfb-night: #160d2e;
    --rfb-nebula: #2c1a57;
    --rfb-glow: #b28cff;
    --rfb-gold: #e6c47a;
    --rfb-parchment: #efe6d2;
    --rfb-ember: #ff9585;

    position: relative;
    width: 100%;
    background: var(--rfb-night);
    color: var(--rfb-parchment);
    font-family: var(--font-family-sans);
  }

  // Past 1600px the art stops growing and its sides melt into the page, so she and the
  // logo never become so large that the form drops far below the fold.
  .rfb-hero {
    position: relative;
    max-width: 1600px;
    margin: 0 auto;

    > img {
      display: block;
      width: 100%;
      height: auto;
      min-height: 220px;
      // Trim only the empty sky above the horns and the dress below the logo: the band
      // from 8% to 94% of the art keeps her face and the whole logo at every width
      // (asserted by the "hero shows the face" e2e tests).
      aspect-ratio: 1600 / 595;
      object-fit: cover;
      object-position: 50% 57%;
    }

    // Melt the art's lower edge into the page colour, below the logo.
    &::after {
      content: '';
      position: absolute;
      inset: auto 0 0 0;
      height: 12%;
      background: linear-gradient(to bottom, transparent, var(--rfb-night));
      pointer-events: none;
    }
  }

  @media (min-width: 1601px) {
    .rfb-hero {
      -webkit-mask-image: linear-gradient(to right, transparent, #000 10%, #000 90%, transparent);
      mask-image: linear-gradient(to right, transparent, #000 10%, #000 90%, transparent);
    }
  }

  .rfb-home {
    position: absolute;
    top: 16px;
    left: 16px;
    z-index: 1;

    img {
      display: block;
      width: 72px;
      height: auto;
      filter: drop-shadow(0 2px 6px rgba(0, 0, 0, 0.6));
    }
  }

  .rfb-panel {
    position: relative;
    z-index: 1;
    box-sizing: border-box;
    width: min(560px, calc(100% - 32px));
    // Overlap the art's faded edge, never the logo just above it (the gap scales with width).
    margin: max(-40px, -3.5vw) auto 0;
    padding: 32px 32px 28px;
    background: rgba(44, 26, 87, 0.92);
    border: 1px solid rgba(230, 196, 122, 0.45);
    border-radius: 6px;
    box-shadow: 0 0 48px rgba(178, 140, 255, 0.18);

    h1 {
      margin: 0 0 12px;
      font-family: var(--font-family-serif);
      font-size: 32px;
      line-height: 1.15;
      color: var(--rfb-gold);
      text-align: center;
      text-shadow: 0 2px 8px rgba(0, 0, 0, 0.5);
    }

    h2 {
      margin: 24px 0 8px;
      font-family: var(--font-family-serif);
      font-size: 19px;
      color: var(--rfb-gold);
    }
  }

  .rfb-intro {
    margin: 0;
    font-size: 17px;
    line-height: 1.5;
    text-align: center;
  }

  .rfb-steps {
    margin: 0;
    padding-left: 22px;
    font-size: 16px;
    line-height: 1.5;

    li + li {
      margin-top: 4px;
    }

    li::marker {
      color: var(--rfb-gold);
      font-weight: 700;
    }
  }

  .rfb-loading {
    display: flex;
    justify-content: center;
    padding: 24px 0 8px;
  }

  .rfb-form {
    margin-top: 24px;

    label {
      display: block;
      margin-bottom: 6px;
      font-size: 15px;
      font-weight: 600;
    }
  }

  .rfb-field {
    position: relative;

    input {
      box-sizing: border-box;
      width: 100%;
      margin: 0;
      padding: 14px 64px 14px 16px;
      font-family: var(--font-family-mono);
      font-size: 22px;
      letter-spacing: 0.16em;
      // The server ignores case; showing capitals makes comparing with the mail easier.
      text-transform: uppercase;
      color: var(--rfb-parchment);
      background: rgba(22, 13, 46, 0.85);
      border: 1px solid rgba(178, 140, 255, 0.5);
      border-radius: 4px;

      &:focus {
        outline: 2px solid var(--rfb-glow);
        outline-offset: 1px;
        box-shadow: 0 0 16px rgba(178, 140, 255, 0.45);
      }
    }

  }

  .rfb-field.complete input {
    border-color: var(--rfb-gold);
  }

  .rfb-count {
    position: absolute;
    top: 50%;
    right: 14px;
    transform: translateY(-50%);
    font-size: 14px;
    font-variant-numeric: tabular-nums;
    color: rgba(239, 230, 210, 0.65);

    .complete & {
      color: var(--rfb-gold);
    }
  }

  .rfb-token-warning {
    margin: 6px 0 0;
    font-size: 14px;
    color: var(--rfb-gold);
  }

  .rfb-hint {
    margin: 6px 0 0;
    font-size: 14px;
    color: rgba(239, 230, 210, 0.75);
  }

  .rfb-error {
    margin: 12px 0 0;
    padding: 10px 12px;
    font-size: 15px;
    color: var(--rfb-ember);
    background: rgba(255, 149, 133, 0.1);
    border-left: 3px solid var(--rfb-ember);
  }

  .rfb-cta {
    display: block;
    width: 100%;
    margin: 18px 0 0;
    padding: 14px 16px;
    font-family: var(--font-family-serif);
    font-size: 19px;
    font-weight: 700;
    color: var(--rfb-night);
    background: linear-gradient(to bottom, #f3d993, var(--rfb-gold) 55%, #c9a24f);
    border: 1px solid #f7e2a8;
    border-radius: 4px;
    cursor: pointer;

    &:hover:not(:disabled) {
      filter: brightness(1.08);
    }

    &:focus-visible {
      outline: 2px solid var(--rfb-glow);
      outline-offset: 2px;
    }

    // global.scss fades every disabled button (opacity .4, a grey !important gradient and a
    // white text-shadow), which left this label unreadable on the dark panel.
    &:disabled {
      opacity: 1;
      cursor: not-allowed;
      text-shadow: none;
      color: rgba(239, 230, 210, 0.7);
      background: #3a2a66 !important;
      border-color: rgba(230, 196, 122, 0.35);
    }
  }

  // wrong_kingdom / not_open / status_error take the form's place: no claim action there.
  .rfb-notice {
    margin-top: 24px;
    padding: 16px 18px;
    text-align: center;
    background: rgba(22, 13, 46, 0.6);
    border: 1px solid rgba(230, 196, 122, 0.45);
    border-radius: 4px;

    h2 {
      margin: 0 0 6px;
    }

    p {
      margin: 0;
      font-size: 16px;
      line-height: 1.5;
    }
  }

  .rfb-secondary {
    margin: 14px 0 0;
    padding: 10px 20px;
    font-family: var(--font-family-serif);
    font-size: 16px;
    font-weight: 700;
    color: var(--rfb-gold);
    background: transparent;
    border: 1px solid var(--rfb-gold);
    border-radius: 4px;
    cursor: pointer;

    &:hover {
      background: rgba(230, 196, 122, 0.12);
    }

    &:focus-visible {
      outline: 2px solid var(--rfb-glow);
      outline-offset: 2px;
    }
  }

  .rfb-account {
    margin: 14px 0 0;
    font-size: 14px;
    line-height: 1.45;
    color: rgba(239, 230, 210, 0.75);
    text-align: center;

    a {
      color: var(--rfb-glow);
      white-space: nowrap;
    }
  }

  .rfb-claimed {
    text-align: center;
  }

  .rfb-result h2 {
    margin: 0 0 8px;
    font-size: 26px;
    line-height: 1.2;
  }

  .rfb-heroes {
    display: block;
    width: 100%;
    height: auto;
    margin-top: 48px;
    -webkit-mask-image: linear-gradient(to bottom, transparent, #000 40%);
    mask-image: linear-gradient(to bottom, transparent, #000 40%);
  }

  .rfb-login-notice {
    box-sizing: border-box;
    margin: 0 0 16px;
    padding: 12px 14px;
    font-size: 15px;
    line-height: 1.45;
    color: #fffce9;
    background: rgba(44, 26, 87, 0.85);
    border: 1px solid rgba(230, 196, 122, 0.5);
    border-radius: 4px;
  }

  @media (max-width: 767px) {
    .rfb-panel {
      padding: 24px 18px 22px;

      h1 {
        font-size: 26px;
      }
    }

    .rfb-intro {
      font-size: 16px;
    }

    .rfb-field input {
      font-size: 19px;
      letter-spacing: 0.1em;
    }

    .rfb-home img {
      width: 52px;
    }

    .rfb-heroes {
      margin-top: 32px;
    }

    .rfb-result h2 {
      font-size: 22px;
    }
  }
</style>
